/**
 * Wire contract between the audit API routes and the UI.
 *
 * Plan: apps/hub/docs/development/entity-audit-log-s3-plan.md
 *   -> "UI specification" for the surface this serves.
 *
 * Declared in one place so the route and the components cannot drift, and so
 * neither side imports the other's internals. Shared with `@wtc/audit` only for
 * the classification vocabulary — the wire shapes themselves are Hub's.
 */

import { z } from "zod";

import type {
  AuditChannel,
  AuditOp,
  AuditSource,
  FieldClass,
  NonRevertableReason,
} from "@west-tennessee-consulting/audit/types";

/** Entity types with audit history today (wave 1). */
export const AUDITABLE_ENTITY_TYPES = [
  "contacts",
  "companies",
  "tickets",
  "deals",
] as const;

export type AuditableEntityType = (typeof AUDITABLE_ENTITY_TYPES)[number];

export function isAuditableEntityType(value: string): value is AuditableEntityType {
  // `.some` with an equality check rather than `.includes`: comparing the union
  // to a string is legal, so no widening cast is needed to satisfy the compiler.
  return AUDITABLE_ENTITY_TYPES.some((entityType) => entityType === value);
}

// ---------------------------------------------------------------------------
// GET /api/audit/[entityType]/[entityId]
// ---------------------------------------------------------------------------

/** One field's before/after, already formatted for display. */
export interface AuditChangeDto {
  field: string;
  /** Human label — never a raw column name in the UI. */
  label: string;
  fieldClass: FieldClass;
  /** Pre-formatted for display. `null` means the value was empty; the UI renders "(empty)". */
  oldDisplay: string | null;
  newDisplay: string | null;
  revertable: boolean;
  nonRevertableReason?: NonRevertableReason;
  /**
   * Element-level breakdown for array/jsonb fields ("Added RMM · Device ID =
   * ABC123"). RECOMPUTED at render from old/new, never read from the log — so
   * improving the differ improves all historical rows retroactively.
   */
  elements?: {
    added: string[];
    removed: string[];
    changed: string[];
  };
}

export interface AuditEventDto {
  eventId: string;
  seq: number;
  op: AuditOp;
  /** DB transaction time, ISO. */
  at: string;
  /** When the append landed in S3, ISO. A wide gap means the drain is behind. */
  recordedAt: string;
  actor: {
    id: string | null;
    /** Denormalized at append time, so a later rename never rewrites history. */
    name: string | null;
    channel: AuditChannel;
  };
  source: AuditSource;
  revertedEventId: string | null;
  changes: AuditChangeDto[];
  /** True when this event can anchor a point-in-time reconstruction. */
  isAnchor: boolean;
  /**
   * False when nothing in this event can be written back (all fields `system` or
   * otherwise non-revertable). The UI hides the restore control rather than
   * offering one that would be rejected.
   */
  anyRevertable: boolean;
}

export interface AuditHistoryResponse {
  entityType: AuditableEntityType;
  entityId: string;
  /** Newest first. */
  events: AuditEventDto[];
  /** Pass as `beforeSeq` to load the next older page. Null = start of history. */
  nextCursor: number | null;
  /**
   * True once a sealed month object had to be fetched — i.e. the user has paged
   * past the live tail. Surfaced so the read-cost contract is observable in
   * practice, not just in tests.
   */
  readSealedMonths: boolean;
}

// ---------------------------------------------------------------------------
// POST /api/audit/restore  (dry run — builds the confirmation)
// ---------------------------------------------------------------------------

/**
 * `mode` is the two-step confirmation, enforced server-side rather than trusted
 * to the client. `"plan"` never writes; `"apply"` writes.
 *
 * The plan is not cached between the two calls: `apply` recomputes it and
 * re-checks staleness, so a value that changed while the dialog was open cannot
 * slip through a confirmation the user read before it went stale.
 */
export type RestoreMode = "plan" | "apply";

export interface RestoreRequest {
  entityType: AuditableEntityType;
  entityId: string;
  eventId: string;
  mode: RestoreMode;
  /** Restore a subset. Omit for every revertable field in the event. */
  fields?: string[];
  /**
   * Required for `mode: "apply"`. Echoes the `planHash` the user actually saw.
   * A mismatch means the record moved under them → 409, re-confirm. This is what
   * makes the confirmation meaningful instead of decorative.
   */
  planHash?: string;
}

/** A field the restore will write. */
export interface RestorePlanField {
  field: string;
  label: string;
  /** What the record holds right now. */
  currentDisplay: string | null;
  /** What it will hold after the restore. */
  restoredDisplay: string | null;
}

/** A field deliberately left alone, with the reason surfaced to the user. */
export interface RestoreRejectedField {
  field: string;
  label: string;
  reason: NonRevertableReason | "not-in-event" | "not-in-policy" | "class-never";
  /** Plain-language explanation, shown verbatim. */
  message: string;
}

/**
 * A field changed AGAIN since the event being reverted. This is the case the
 * user cannot otherwise see, so it is called out by name with the value that
 * would be discarded.
 */
export interface RestoreStaleField {
  field: string;
  label: string;
  discardedDisplay: string | null;
}

export type RestorePlanKind = "field-revert" | "undelete" | "blocked";

export interface RestorePlanResponse {
  kind: RestorePlanKind;
  entityType: AuditableEntityType;
  entityId: string;
  eventId: string;
  /** Echo back with `mode: "apply"`. Changes if the record moves. */
  planHash: string;
  fields: RestorePlanField[];
  rejected: RestoreRejectedField[];
  stale: RestoreStaleField[];
  /** Set when `kind === "blocked"` — e.g. reverting an INSERT means deleting. */
  blockedReason?: string;
  /** Undelete only. Admin-gated, and cascade-deleted children do not return. */
  requiresAdmin: boolean;
  /** Extra warnings to surface verbatim (collision risk, missing FK referent). */
  cautions: string[];
}

export interface RestoreAppliedResponse {
  applied: true;
  entityType: AuditableEntityType;
  entityId: string;
  /** The event that was reverted. */
  revertedEventId: string;
  fieldsWritten: string[];
  /**
   * Consequences of the write that the user must still be told about AFTER it
   * succeeded — today, that cascade-deleted children of an undeleted record did
   * not come back with it.
   *
   * Deliberately on the APPLIED response and not only on the plan: a success
   * that says nothing implies the record came back whole, which for an undelete
   * is false. Optional so an older client is unaffected.
   */
  cautions?: string[];
}

export type RestoreResponse = RestorePlanResponse | RestoreAppliedResponse;

export function isRestoreApplied(
  response: RestoreResponse
): response is RestoreAppliedResponse {
  return "applied" in response && response.applied === true;
}

// ---------------------------------------------------------------------------
// Runtime validation of the wire payloads
// ---------------------------------------------------------------------------

/**
 * `response.json()` is `any`, so annotating it with `as AuditHistoryResponse`
 * type-checks against a shape nobody verified — the compiler is agreeing with an
 * assertion, not proving anything. If the route's shape ever drifts from this
 * contract the UI would render wrong history silently, which in an audit feature
 * is the worst possible failure mode.
 *
 * These schemas are the same contract expressed once more in a form that runs.
 * They are deliberately tolerant about ADDITIONS (no `.strict()`) so a newer
 * route can add a field without breaking an older client, and strict about the
 * fields the UI actually reads.
 */

const auditChangeSchema = z.object({
  field: z.string(),
  label: z.string(),
  fieldClass: z.enum(["never", "system", "semantic", "plain"]),
  oldDisplay: z.string().nullable(),
  newDisplay: z.string().nullable(),
  revertable: z.boolean(),
  nonRevertableReason: z
    .enum(["derived", "unique-constrained", "foreign-key", "secret"])
    .optional(),
  elements: z
    .object({
      added: z.array(z.string()),
      removed: z.array(z.string()),
      changed: z.array(z.string()),
    })
    .optional(),
});

const auditEventSchema = z.object({
  eventId: z.string(),
  seq: z.number(),
  op: z.enum(["INSERT", "UPDATE", "DELETE"]),
  at: z.string(),
  recordedAt: z.string(),
  actor: z.object({
    id: z.string().nullable(),
    name: z.string().nullable(),
    channel: z.enum([
      "ui",
      "meridian",
      "slack",
      "mcp",
      "cron",
      "migration",
      "unknown",
    ]),
  }),
  source: z.enum(["user", "revert"]),
  revertedEventId: z.string().nullable(),
  changes: z.array(auditChangeSchema),
  isAnchor: z.boolean(),
  anyRevertable: z.boolean(),
});

export const auditHistoryResponseSchema = z.object({
  entityType: z.enum(AUDITABLE_ENTITY_TYPES),
  entityId: z.string(),
  events: z.array(auditEventSchema),
  nextCursor: z.number().nullable(),
  readSealedMonths: z.boolean(),
});

const restorePlanFieldSchema = z.object({
  field: z.string(),
  label: z.string(),
  currentDisplay: z.string().nullable(),
  restoredDisplay: z.string().nullable(),
});

const restorePlanResponseSchema = z.object({
  kind: z.enum(["field-revert", "undelete", "blocked"]),
  entityType: z.enum(AUDITABLE_ENTITY_TYPES),
  entityId: z.string(),
  eventId: z.string(),
  planHash: z.string(),
  fields: z.array(restorePlanFieldSchema),
  rejected: z.array(
    z.object({
      field: z.string(),
      label: z.string(),
      // Enum + `.catch`, not `z.string()`: a bare string does not satisfy
      // `RestoreRejectedField.reason`, so `parseRestoreResponse` was claiming a
      // narrower type than it proved. `.catch` keeps the tolerance that was the
      // point of the loose type — an unknown reason from a newer route degrades
      // to "not-in-policy" (the UI reads `message`, which is what it renders)
      // instead of failing the whole parse.
      reason: z
        .enum([
          "derived",
          "unique-constrained",
          "foreign-key",
          "secret",
          "not-in-event",
          "not-in-policy",
          "class-never",
        ])
        .catch("not-in-policy"),
      message: z.string(),
    })
  ),
  stale: z.array(
    z.object({
      field: z.string(),
      label: z.string(),
      discardedDisplay: z.string().nullable(),
    })
  ),
  blockedReason: z.string().optional(),
  requiresAdmin: z.boolean(),
  cautions: z.array(z.string()),
});

const restoreAppliedResponseSchema = z.object({
  applied: z.literal(true),
  entityType: z.enum(AUDITABLE_ENTITY_TYPES),
  entityId: z.string(),
  revertedEventId: z.string(),
  fieldsWritten: z.array(z.string()),
  // Optional: a field-revert carries none, an undelete carries the cascade note.
  cautions: z.array(z.string()).optional(),
});

/** Applied first: it is the narrower shape, distinguished by `applied: true`. */
export const restoreResponseSchema = z.union([
  restoreAppliedResponseSchema,
  restorePlanResponseSchema,
]);

/** Parse a history payload, or throw with the field path that failed. */
export function parseAuditHistoryResponse(payload: unknown): AuditHistoryResponse {
  return auditHistoryResponseSchema.parse(payload);
}

/** Parse a restore payload (plan or applied), or throw with the failing path. */
export function parseRestoreResponse(payload: unknown): RestoreResponse {
  return restoreResponseSchema.parse(payload);
}
